import React, { useState, useEffect, useCallback } from 'react';
import { 
  FiFileText, FiBook, FiUser, FiMail, FiCalendar, 
  FiCheck, FiX, FiClock, FiMessageSquare, FiFilter,
  FiChevronDown, FiSearch, FiBookOpen, FiAward, FiRefreshCw
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import './Sitasi.css';

const Sitasi = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('pending'); // pending, approved, rejected, all
  const [activeType, setActiveType] = useState('all'); // all, buku, jurnal
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [bukuRequests, setBukuRequests] = useState([]);
  const [jurnalRequests, setJurnalRequests] = useState([]);
  const [bukuStats, setBukuStats] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [jurnalStats, setJurnalStats] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  
  // Action states
  const [processingRequest, setProcessingRequest] = useState(null);
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [notes, setNotes] = useState({});
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Search
  const [searchQuery, setSearchQuery] = useState('');

  const fetchBukuRequests = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const response = await fetch(`/api/user/request_sitasi.php?user_id=${user.id}`);
      const data = await response.json();
      
      if (data.success) {
        setBukuRequests(data.data || []);
        setBukuStats(data.stats || { pending: 0, approved: 0, rejected: 0, total: 0 });
      }
    } catch (error) {
      console.error('Error fetching buku requests:', error);
    }
  }, [user?.id]);

  const fetchJurnalRequests = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const response = await fetch(`/api/user/request_sitasi_jurnal.php?action=dosen&user_id=${user.id}`);
      const data = await response.json();
      
      if (data.success) {
        setJurnalRequests(data.data || []);
        setJurnalStats(data.stats || { pending: 0, approved: 0, rejected: 0, total: 0 });
      }
    } catch (error) {
      console.error('Error fetching jurnal requests:', error);
    }
  }, [user?.id]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchBukuRequests(), fetchJurnalRequests()]);
    setLoading(false);
  }, [fetchBukuRequests, fetchJurnalRequests]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleAction = async (requestId, type, status) => {
    setProcessingRequest(`${type}_${requestId}`);
    
    const apiUrl = type === 'buku' 
      ? '/api/user/request_sitasi.php'
      : '/api/user/request_sitasi_jurnal.php';
    
    try {
      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_request: requestId,
          user_id: user.id,
          status: status,
          catatan_dosen: notes[`${type}_${requestId}`] || ''
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setMessage({ type: 'success', text: `Request berhasil ${status === 'approved' ? 'disetujui' : 'ditolak'}!` });
        setExpandedRequest(null);
        setNotes(prev => ({ ...prev, [`${type}_${requestId}`]: '' }));
        fetchAllData();
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Gagal memproses request' });
    } finally {
      setProcessingRequest(null);
    }
  };

  // Combine and filter requests
  const getAllRequests = () => {
    const allRequests = [];
    
    if (activeType === 'all' || activeType === 'buku') {
      bukuRequests.forEach(r => allRequests.push({ ...r, type: 'buku' }));
    }
    
    if (activeType === 'all' || activeType === 'jurnal') {
      jurnalRequests.forEach(r => allRequests.push({ ...r, type: 'jurnal' }));
    }
    
    // Filter by status
    let filtered = allRequests;
    if (activeTab !== 'all') {
      filtered = allRequests.filter(r => r.status === activeTab);
    }
    
    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => 
        (r.judul_buku?.toLowerCase().includes(query)) ||
        (r.judul_jurnal?.toLowerCase().includes(query)) ||
        (r.nama_mahasiswa?.toLowerCase().includes(query)) ||
        (r.nim?.toLowerCase().includes(query))
      );
    }
    
    // Sort by date
    filtered.sort((a, b) => new Date(b.tanggal_request) - new Date(a.tanggal_request));
    
    return filtered;
  };

  const totalStats = {
    pending: bukuStats.pending + jurnalStats.pending,
    approved: bukuStats.approved + jurnalStats.approved,
    rejected: bukuStats.rejected + jurnalStats.rejected,
    total: bukuStats.total + jurnalStats.total
  };

  const requests = getAllRequests();

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="sitasi">
      {/* Header */}
      <div className="sitasi__header">
        <div className="sitasi__header-content">
          <h1><FiAward /> Request Sitasi</h1>
          <p>Kelola request sitasi buku dan jurnal dari mahasiswa</p>
        </div>
        <button className="sitasi__refresh-btn" onClick={fetchAllData} disabled={loading}>
          <FiRefreshCw className={loading ? 'spinning' : ''} /> Refresh
        </button>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`sitasi__message sitasi__message--${message.type}`}>
          {message.type === 'success' ? <FiCheck /> : <FiX />}
          {message.text}
          <button onClick={() => setMessage({ type: '', text: '' })}><FiX /></button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="sitasi__stats">
        <div className="sitasi__stat sitasi__stat--pending" onClick={() => setActiveTab('pending')}>
          <div className="sitasi__stat-icon"><FiClock /></div>
          <div className="sitasi__stat-content">
            <span className="sitasi__stat-value">{totalStats.pending}</span>
            <span className="sitasi__stat-label">Menunggu</span>
          </div>
        </div>
        
        <div className="sitasi__stat sitasi__stat--approved" onClick={() => setActiveTab('approved')}>
          <div className="sitasi__stat-icon"><FiCheck /></div>
          <div className="sitasi__stat-content">
            <span className="sitasi__stat-value">{totalStats.approved}</span>
            <span className="sitasi__stat-label">Disetujui</span>
          </div>
        </div>
        
        <div className="sitasi__stat sitasi__stat--rejected" onClick={() => setActiveTab('rejected')}>
          <div className="sitasi__stat-icon"><FiX /></div>
          <div className="sitasi__stat-content">
            <span className="sitasi__stat-value">{totalStats.rejected}</span>
            <span className="sitasi__stat-label">Ditolak</span>
          </div>
        </div>
        
        <div className="sitasi__stat sitasi__stat--total" onClick={() => setActiveTab('all')}>
          <div className="sitasi__stat-icon"><FiBookOpen /></div>
          <div className="sitasi__stat-content">
            <span className="sitasi__stat-value">{totalStats.total}</span>
            <span className="sitasi__stat-label">Total</span>
          </div>
        </div>
      </div>

      {/* Mini Stats per Type */}
      <div className="sitasi__type-stats">
        <div className="sitasi__type-stat">
          <FiBook />
          <span><strong>{bukuStats.total}</strong> Request Buku</span>
          <span className="sitasi__type-stat-pending">{bukuStats.pending} menunggu</span>
        </div>
        <div className="sitasi__type-stat">
          <FiFileText />
          <span><strong>{jurnalStats.total}</strong> Request Jurnal</span>
          <span className="sitasi__type-stat-pending">{jurnalStats.pending} menunggu</span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="sitasi__toolbar">
        <div className="sitasi__search">
          <FiSearch />
          <input 
            type="text"
            placeholder="Cari judul, nama mahasiswa, atau NIM..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="sitasi__filters">
          <div className="sitasi__filter-group">
            <label>Status:</label>
            <select value={activeTab} onChange={(e) => setActiveTab(e.target.value)}>
              <option value="all">Semua</option>
              <option value="pending">Menunggu</option>
              <option value="approved">Disetujui</option>
              <option value="rejected">Ditolak</option>
            </select>
          </div>
          
          <div className="sitasi__filter-group">
            <label>Tipe:</label>
            <select value={activeType} onChange={(e) => setActiveType(e.target.value)}>
              <option value="all">Semua</option>
              <option value="buku">Buku</option>
              <option value="jurnal">Jurnal</option>
            </select>
          </div>
        </div>
      </div>

      {/* Request List */}
      {loading ? (
        <div className="sitasi__loading">
          <div className="sitasi__spinner"></div>
          <p>Memuat data...</p>
        </div>
      ) : requests.length === 0 ? (
        <div className="sitasi__empty">
          <FiAward />
          <h3>Tidak ada request</h3>
          <p>Belum ada request sitasi yang sesuai dengan filter</p>
        </div>
      ) : (
        <div className="sitasi__list">
          {requests.map((request) => {
            const requestKey = `${request.type}_${request.id_request}`;
            const isExpanded = expandedRequest === requestKey;
            const isProcessing = processingRequest === requestKey;
            const title = request.type === 'buku' ? request.judul_buku : request.judul_jurnal;
            
            return (
              <div key={requestKey} className={`sitasi__item sitasi__item--${request.status}`}>
                <div className="sitasi__item-header">
                  <div className="sitasi__item-type">
                    {request.type === 'buku' ? <FiBook /> : <FiFileText />}
                    <span>{request.type === 'buku' ? 'Buku' : 'Jurnal'}</span>
                  </div>
                  <span className={`sitasi__item-status sitasi__item-status--${request.status}`}>
                    {request.status === 'pending' ? <><FiClock /> Menunggu</> : 
                     request.status === 'approved' ? <><FiCheck /> Disetujui</> : 
                     <><FiX /> Ditolak</>}
                  </span>
                </div>
                
                <h3 className="sitasi__item-title">{title}</h3>
                
                <div className="sitasi__item-info">
                  <div className="sitasi__item-row">
                    <FiUser />
                    <span><strong>Mahasiswa:</strong> {request.nama_mahasiswa || 'Unknown'}</span>
                  </div>
                  <div className="sitasi__item-row">
                    <FiFileText />
                    <span><strong>NIM:</strong> {request.nim || '-'}</span>
                  </div>
                  <div className="sitasi__item-row">
                    <FiMail />
                    <span><strong>Email:</strong> {request.email_mahasiswa || '-'}</span>
                  </div>
                  <div className="sitasi__item-row">
                    <FiBookOpen />
                    <span><strong>Keperluan:</strong> {request.keperluan}</span>
                  </div>
                  <div className="sitasi__item-row">
                    <FiCalendar />
                    <span><strong>Tanggal:</strong> {formatDate(request.tanggal_request)}</span>
                  </div>
                  {request.catatan && (
                    <div className="sitasi__item-row sitasi__item-row--note">
                      <FiMessageSquare />
                      <span><strong>Catatan:</strong> {request.catatan}</span>
                    </div>
                  )}
                </div>
                
                {/* Actions for pending requests */}
                {request.status === 'pending' && (
                  <div className="sitasi__item-actions">
                    {isExpanded ? (
                      <div className="sitasi__action-form">
                        <div className="sitasi__action-form-input">
                          <label>
                            <FiMessageSquare /> Catatan untuk Mahasiswa (opsional):
                          </label>
                          <textarea 
                            placeholder="Tuliskan catatan atau alasan..."
                            value={notes[requestKey] || ''}
                            onChange={(e) => setNotes(prev => ({
                              ...prev,
                              [requestKey]: e.target.value
                            }))}
                            rows={3}
                          />
                        </div>
                        <div className="sitasi__action-form-buttons">
                          <button 
                            className="sitasi__btn sitasi__btn--cancel"
                            onClick={() => {
                              setExpandedRequest(null);
                              setNotes(prev => ({ ...prev, [requestKey]: '' }));
                            }}
                          >
                            Batal
                          </button>
                          <button 
                            className="sitasi__btn sitasi__btn--approve"
                            onClick={() => handleAction(request.id_request, request.type, 'approved')}
                            disabled={isProcessing}
                          >
                            {isProcessing ? 'Memproses...' : <><FiCheck /> Setujui</>}
                          </button>
                          <button 
                            className="sitasi__btn sitasi__btn--reject"
                            onClick={() => handleAction(request.id_request, request.type, 'rejected')}
                            disabled={isProcessing}
                          >
                            {isProcessing ? 'Memproses...' : <><FiX /> Tolak</>}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="sitasi__quick-actions">
                        <button 
                          className="sitasi__btn sitasi__btn--approve"
                          onClick={() => setExpandedRequest(requestKey)}
                        >
                          <FiCheck /> Setujui
                        </button>
                        <button 
                          className="sitasi__btn sitasi__btn--reject"
                          onClick={() => setExpandedRequest(requestKey)}
                        >
                          <FiX /> Tolak
                        </button>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Response info for processed requests */}
                {request.status !== 'pending' && request.tanggal_respon && (
                  <div className="sitasi__item-response">
                    <small>Direspon pada: {formatDate(request.tanggal_respon)}</small>
                    {request.catatan_dosen && (
                      <div className="sitasi__item-response-note">
                        <strong>Catatan Anda:</strong>
                        <p>{request.catatan_dosen}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Sitasi;
